import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Spin,
  Row,
  Col,
  Card,
  Avatar,
  Tag,
  Typography,
  Space,
  Divider,
} from "antd";
import {
  SearchBar,
  HorizontalScroller,
  BookingModal,
  useToast,
} from "../../components";
import { topic, availability as availabilityApi } from "../../store";
import axios from "../../axios";
import dayjs from "dayjs";

// Import calendar components
const TopicDetail = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    selectedTopic,
    isLoading,
    error,
    topicCoaches,
    coachesLoading,
    coachesError,
    coachesPagination,
  } = useSelector((state) => state.activeTopics);

  const { availability, availabilityLoading, availabilityError } = useSelector(
    (state) => state.availability
  );

  // Generate 7 days starting from today
  const [weekDays, setWeekDays] = useState([]);

  // Selected date index
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  const toast = useToast();

  // Booking Modal State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState({
    coach: null,
    slot: null,
    date: null,
  });

  // No longer need refs and scroll functions - using HorizontalScroller component

  useEffect(() => {
    // Generate week days
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(dayjs().add(i, "day"));
    }
    setWeekDays(days);
  }, []);

  // Load topic data on mount
  useEffect(() => {
    if (topicId) {
      dispatch(topic.getTopicById(topicId));
      dispatch(topic.loadMoreCoaches(topicId, 0)); // Initial load
      // Dispatch availability API with today's date by default
      const today = dayjs().format("YYYY-MM-DD");
      dispatch(
        availabilityApi.getCoachesAvailabilityApi({
          topicId,
          date: today,
        })
      );
    }
  }, [topicId, dispatch]);

  // Handle scroll for coaches loading
  const handleCoachesScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        coachesPagination.hasMore &&
        !coachesLoading
      ) {
        dispatch(topic.loadMoreCoaches(topicId, coachesPagination.skip + 10));
      }
    },
    [coachesPagination, coachesLoading, topicId, dispatch]
  );

  // Handle time slot click
  const handleSlotClick = (coach, slot) => {
    const selectedDate = weekDays[selectedDayIndex];
    setSelectedBooking({
      coach: coach,
      slot: slot,
      date: selectedDate.format("dddd, DD MMMM"),
    });
    setIsModalVisible(true);
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedBooking({
      coach: null,
      slot: null,
      date: null,
    });
  };

  // Handle modal next
  const handleModalNext = async () => {
    try {
      const payload = {
        availabilityId: selectedBooking.slot.id,
        topicId,
      };
      const response = await axios.post("/appointments", payload);
      if (response.status === 201) {
        toast.success(
          "Booking Successful",
          `Booking successful with coach ${selectedBooking.coach.name}`
        );

        // Reload availability for the same date after successful booking
        const currentDate = weekDays[selectedDayIndex].format("YYYY-MM-DD");
        dispatch(
          availabilityApi.getCoachesAvailabilityApi({
            topicId,
            date: currentDate,
          })
        );
      }
      setIsModalVisible(false);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An error occurred while booking";
      toast.error("Booking Failed", errorMessage);
    }
  };

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error("Error", error);
    }
    if (coachesError) {
      toast.error("Coaches Error", coachesError);
    }
    if (availabilityError) {
      toast.error("Availability Error", availabilityError);
    }
  }, [error, coachesError, availabilityError, toast]);

  if (isLoading && !selectedTopic) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!selectedTopic) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <h2>Topic not found</h2>
        <button onClick={() => navigate("/explore")}>Go back</button>
      </div>
    );
  }

  return (
    <div>
      <SearchBar />
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        {/* Topic Title */}
        <Typography.Title level={1} style={{ marginBottom: "10px" }}>
          {selectedTopic.name}
        </Typography.Title>

        {/* Topic Description */}
        <Typography.Paragraph
          style={{
            fontSize: "16px",
            // textAlign: "left",
            color: "#666",
            margin: "0 auto",
            maxWidth: "800px",
          }}
        >
          {selectedTopic.description}
        </Typography.Paragraph>

        {/* Select Date Section */}
        <div style={{ marginBottom: "40px" }}>
          <Typography.Title level={2} style={{ marginBottom: "20px" }}>
            Select Date
          </Typography.Title>

          {/* Date strip using Ant Design Row/Col */}
          <Card
            style={{
              backgroundColor: "#CCFBF1",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
            styles={{ body: { padding: "16px" } }}
          >
            <Row gutter={[8, 8]} justify="space-around">
              {weekDays.map((day, index) => (
                <Col
                  key={index}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                  xl={1}
                  style={{ textAlign: "center" }}
                >
                  <Card
                    size="small"
                    style={{
                      width: "5em",
                      cursor: "pointer",
                      backgroundColor:
                        selectedDayIndex === index ? "#FFFFFF" : "transparent",
                      border:
                        selectedDayIndex === index
                          ? "2px solid #008080"
                          : "2px solid white",
                      transition: "all 0.2s ease",
                    }}
                    styles={{
                      body: {
                        padding: "8px",
                        textAlign: "center",
                        color:
                          selectedDayIndex === index ? "#000000" : "#008080",
                      },
                    }}
                    onClick={() => {
                      setSelectedDayIndex(index);
                      const date = weekDays[index].format("YYYY-MM-DD");
                      dispatch(
                        availabilityApi.getCoachesAvailabilityApi({
                          topicId,
                          date,
                        })
                      );
                    }}
                  >
                    <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                      {day.format("DD")}
                    </div>
                    <div style={{ fontSize: "14px" }}>{day.format("MMM")}</div>
                    <div style={{ fontSize: "12px", marginTop: "4px" }}>
                      {day.format("ddd")}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Availability Section - Show when availability data is available */}
          {!availability.length > 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                color: "#666",
              }}
            >
              <Typography.Text type="secondary">
                No coaches available for this topic for the selected date
              </Typography.Text>
            </div>
          ) : (
            <Card
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
              styles={{ body: { padding: "20px" } }}
            >
              <Row gutter={[16, 16]}>
                {availability.map((coachData, coachIndex) => (
                  <Col
                    key={coachData.coach.id}
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                  >
                    <Row gutter={[32, 8]} align="middle" wrap={false}>
                      {/* Coach Picture and Name - clickable to coach detail */}
                      <Col flex="none">
                        <Space
                          align="center"
                          size="middle"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate(`/coach/${coachData.coach.id}`)
                          }
                        >
                          <Avatar
                            size={60}
                            src={
                              coachData.coach.picture || "/default-avatar.png"
                            }
                            alt={coachData.coach.name}
                            style={{ border: "2px solid #ddd" }}
                          />
                          <Typography.Title
                            level={5}
                            style={{
                              margin: 0,
                              minWidth: "120px",
                              maxWidth: "180px",
                            }}
                          >
                            {coachData.coach.name}
                          </Typography.Title>
                        </Space>
                      </Col>

                      {/* Time Slots - Using HorizontalScroller Component */}
                      <Col flex="auto" style={{ minWidth: 0 }}>
                        <HorizontalScroller>
                          {coachData.availability.map((slot, slotIndex) => (
                            <Tag
                              key={slotIndex}
                              color="cyan"
                              style={{
                                minWidth: "120px",
                                textAlign: "center",
                                padding: "6px 12px",
                                fontSize: "14px",
                                fontWeight: "bold",
                                borderRadius: "6px",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleSlotClick(coachData.coach, slot)
                              }
                            >
                              {slot.startTime} - {slot.endTime}
                            </Tag>
                          ))}
                        </HorizontalScroller>
                      </Col>
                    </Row>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </div>

        {/* Divider between sections */}
        <Divider />

        {/* Coaches Section */}
        <div>
          <Typography.Title level={2} style={{ marginBottom: "20px" }}>
            Coaches offering {selectedTopic.name}
          </Typography.Title>

          <Card
            style={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
            styles={{ body: { padding: "20px" } }}
          >
            <div
              style={{
                height: "fit-content",
              }}
              onScroll={
                !(topicCoaches.length > 0) ? handleCoachesScroll : undefined
              }
            >
              {availabilityLoading && (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="large" />
                </div>
              )}

              {!coachesLoading && topicCoaches.length > 0 && (
                <Row gutter={[16, 16]} justify="start">
                  {topicCoaches.map((item, index) => (
                    <Col
                      key={item.id || index}
                      xs={12}
                      sm={8}
                      md={6}
                      lg={4}
                      xl={3}
                      style={{ textAlign: "center" }}
                    >
                      <Card
                        hoverable
                        style={{
                          borderRadius: "8px",
                          textAlign: "center",
                        }}
                        styles={{ body: { padding: "12px" } }}
                        onClick={() => navigate(`/coach/${item.id}`)}
                      >
                        <Avatar
                          size={60}
                          src={item.picture || "/default-avatar.png"}
                          alt={item.name}
                          style={{
                            border: "2px solid #ddd",
                            marginBottom: "8px",
                          }}
                        />
                        <Typography.Text
                          style={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            display: "block",
                          }}
                        >
                          {item.name}
                        </Typography.Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {!coachesLoading && topicCoaches.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#666",
                  }}
                >
                  <Typography.Text type="secondary">
                    No coaches available for this topic
                  </Typography.Text>
                </div>
              )}

              {coachesLoading && (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="large" />
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onNext={handleModalNext}
        coachData={selectedBooking.coach}
        slotData={selectedBooking.slot}
        selectedDate={selectedBooking.date}
        topicData={selectedTopic.name}
      />
    </div>
  );
};

export default TopicDetail;
